import { useTranslation } from "react-i18next";
import { useActiveServices } from "../../../store/adminDataStore";

interface ServiceStepProps {
  selected: string;
  onSelect: (service: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function ServiceStep({ selected, onSelect, onNext, onBack }: ServiceStepProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split("-")[0] ?? "en") as "zh" | "en" | "ja";
  // Reads from services table — only is_active = true, sorted by sort_order
  const services = useActiveServices();

  return (
    <div className="w-full max-w-3xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] mb-3" style={{ letterSpacing: "-0.02em" }}>
          {t("tabifoto.booking.select_service")}
        </h2>
        <div className="w-8 h-px bg-[#C8B8A6] mx-auto" />
      </div>

      <div className="flex flex-col gap-5">
        {services.map((service) => (
          <div
            key={service.id}
            onClick={() => onSelect(service.service_code)}
            className={`group flex gap-0 overflow-hidden rounded-lg border cursor-pointer transition-all duration-300 ${
              selected === service.service_code
                ? "border-[#C8B8A6] bg-[#C8B8A6]/5"
                : "border-[#D9D9D9] hover:border-[#C8B8A6]/50 bg-white"
            }`}
          >
            <div className="shrink-0 w-44 overflow-hidden">
              <img
                src={service.cover_image_url}
                alt={service[`title_${lang}`]}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>

            <div className="flex-1 p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full border border-[#C8B8A6]/50 text-[#C8B8A6]">
                    <i className={service.icon} />
                  </div>
                  <h3 className="text-lg font-light text-[#2C2C2C]">
                    {service[`title_${lang}`]}
                  </h3>
                </div>
                <p className="text-sm text-[#2C2C2C]/50 font-light leading-relaxed mb-4">
                  {service[`short_desc_${lang}`]}
                </p>
                <ul className="space-y-1.5">
                  {service.features.map((feat, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs text-[#2C2C2C]/50">
                      <div className="w-3 h-3 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-[#C8B8A6] text-xs" />
                      </div>
                      {feat[lang]}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-between items-center mt-4">
                <span className="text-xs uppercase tracking-widest text-[#C8B8A6]">
                  {service.base_price_twd > 0
                    ? `NT$${service.base_price_twd.toLocaleString()}`
                    : t("tabifoto.services.from_price")}
                </span>
                {selected === service.service_code && (
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C8B8A6]">
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-10">
        <button onClick={onBack}
          className="px-6 py-3 rounded-full border border-[#D9D9D9] text-sm tracking-widest uppercase text-[#2C2C2C]/50 hover:border-[#2C2C2C] hover:text-[#2C2C2C] transition-all duration-200 whitespace-nowrap cursor-pointer">
          ← {t("tabifoto.booking.back")}
        </button>
        <button onClick={onNext} disabled={!selected}
          className="px-8 py-3 rounded-full bg-[#2C2C2C] text-white text-sm tracking-widest uppercase hover:bg-[#2C2C2C]/80 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
          {t("tabifoto.booking.next")} →
        </button>
      </div>
    </div>
  );
}
